import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, SearchX } from "lucide-react";
import { useMerchants } from "../hooks";
import { useDebounce } from "@/shared/lib/useDebounce";
import { Avatar } from "@/shared/ui/Avatar";
import { Input } from "@/shared/ui/Input";
import type { Merchant } from "../types";

// Step 1 — pick the merchant whose clients/agreements will be imported.
// Debounced server search, skeleton loading, avatar rows, composed empty-state.
export function MerchantStep({ onSelect }: { onSelect: (m: Merchant) => void }) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 350);
  const { data, isLoading, isFetching } = useMerchants(search || undefined);

  const results = data?.results ?? [];

  return (
    <div className="w-full">
      <h2 className="mb-1 text-[18px] font-semibold text-grey-800">
        {t("import.merchantStep.title")}
      </h2>
      <p className="mb-5 text-[13px] text-grey-500">{t("import.merchantStep.subtitle")}</p>

      {/* Search */}
      <div className="relative mb-3">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-400"
        />
        <Input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("import.merchantStep.searchPlaceholder")}
          className="pl-9 pr-4"
        />
      </div>

      {/* Result count — suppressed only during the first load (skeleton handles that). */}
      {!isLoading && (
        <p className="mb-2 px-1 text-[12px] text-grey-400 tnum">
          {t("import.merchantStep.count", { count: results.length })}
        </p>
      )}

      {/* List / skeleton / empty-state */}
      {isLoading ? (
        <ul className="divide-y divide-grey-100 overflow-hidden rounded-card border border-grey-200 bg-white">
          {Array.from({ length: 5 }).map((_, i) => (
            <li key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="h-8 w-8 animate-pulse rounded-pill bg-grey-100" />
              <div className="flex-1">
                <div className="h-3 w-32 animate-pulse rounded bg-grey-100" />
                <div className="mt-1.5 h-2.5 w-24 animate-pulse rounded bg-grey-100" />
              </div>
              <div className="h-5 w-16 animate-pulse rounded-pill bg-grey-100" />
            </li>
          ))}
        </ul>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-grey-200 bg-grey-25 py-12 text-center">
          <SearchX size={28} className="text-grey-300" strokeWidth={1.6} />
          <p className="text-[13px] text-grey-500">{t("import.merchantStep.empty")}</p>
        </div>
      ) : (
        <ul
          className={`divide-y divide-grey-100 overflow-hidden rounded-card border border-grey-200 bg-white transition-opacity ${
            isFetching ? "opacity-60" : ""
          }`}
        >
          {results.map((m) => {
            const name = `${m.first_name} ${m.last_name}`.trim();
            return (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => onSelect(m)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-grey-50 active:bg-grey-100"
                >
                  <Avatar name={name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-medium text-grey-800">{name}</div>
                    <div className="text-[12px] text-grey-400">{m.phone_number}</div>
                  </div>
                  <span className="shrink-0 rounded-pill bg-grey-100 px-2.5 py-0.5 text-[12px] text-grey-600 tnum">
                    {t("import.merchantStep.clientCount", { count: m.client_count })}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
